import { useEffect, useState } from "react"
import { Link, useLocation } from "react-router"
import { ArrowRight, Check, ChevronDown, Copy } from "lucide-react"

import { cn, slugify } from "~/lib/utils"

import { capture } from "~/client/posthog.client"
import { ButtonLink, buttonClasses } from "~/components/ui/button"
import { BOOK_ID, useTrack } from "./track"
import { BANNER_ART } from "~/components/landing/ForestBackdrops"

// Five questions to send the current provider before choosing
// a data partner (less about renewal: Thomas 2026-11-04),
// each with how Pine59 answers it, and one action: copy the questions into an
// email. Nothing to tap or score (Anna 2026-11-02: the "Not sure" toggles and
// "Your provider: 0 of 5" read as childish); the visitor does the asking.
//
// The questions are written as the buyer would send them ("we", "our"), in
// concrete words: "past numbers change without notice", not "history stays
// put". Every Pine59 answer is proven in the approved posts or public Docs.
//
// Laid out like Linear's and Clay's FAQs (Anna 2026-11-02: "heavyyy"): the
// title and actions on the left, the questions on the right as a quiet list
// with hairlines, answers closed. Each closed row still shows Pine59's short
// answer beside the lime mark, so the case reads at a glance; opening a row
// gives the full answer.
//
// The same five questions also go into articles as ProviderQuestions (below):
// first in the how-to-choose post, the one blog post that earns search
// traffic (Anna 2026-11-04). One list, one copy text and one copy button for
// both, so a question changed here changes everywhere.

type Check = {
  q: string
  short: string
  a: string
  // the comparison with named providers, conquest page only
  vs?: string
  link?: { label: string; href: string }
}

const CHECKS: Check[] = [
  {
    q: "Can we share the data with our clients and use it in our own products?",
    short: "Yes",
    a: "Yes. Contract terms allow client reports and products built on the data, and showing the numbers with credit to Pine59.",
    // The comparison, on the conquest page only (Anna 2026-11-05: "fold it
    // in", after Thomas asked to use Placer and Advan more): from their own
    // public terms. Placer.ai terms of service (updated 27 Dec 2022) 3.1,
    // data "solely for your internal business purposes"; Advan terms of
    // service (updated 10 May 2024) 1(e), no using or incorporating the
    // content in "any product or service provided to a third party".
    // Negotiated contracts can differ. LAUNCH CHECK: Thomas reads this line.
    vs: "By their public terms, Placer.ai keeps the data to internal use, and Advan rules it out of any product or service for a third party.",
  },
  {
    // Every question is asked so Pine59's answer is Yes (Anna 2026-11-03,
    // after the Sarah and Kjartan review): a column of five Yes reads at a
    // glance, where one "No" made the reader stop and parse.
    q: "When you update your model, will we hear in advance what changed and why?",
    short: "Yes",
    a: "Yes. Past estimates change only with a major model update, and customers hear in advance what changed and why.",
  },
  {
    // A test on locations the team knows (Anna 2026-11-05). It replaced "If
    // we leave, can we keep using the reports we've already made?": Terms
    // §7.3 only spares derivative works, and §1 lists report preparation
    // apart from them, so that Yes came with caveats and set no one apart.
    // The team suggests how to test, it doesn't promise to run one for
    // everyone (capacity). No link: the section's own Book a meeting button
    // sits right under the list.
    q: "Can we test it on our own locations before we sign?",
    short: "Yes",
    a: "Yes. Bring a few locations you know well, and the Pine59 team suggests the best way to test them.",
  },
  {
    // Asked as the buyer asks it, about accuracy, not about a field (Sarah,
    // via Anna 2026-11-04). Confidence scores cover foot traffic and visitor
    // journey; every place carries polygon_confidence_score in the Docs.
    // "For custom locations too" is out (Anna 2026-11-05: "use safer"): only
    // a #product thread (Tor Åge, 2026-09-16) backs it, not the Docs.
    q: "Can we understand how accurate the numbers are?",
    short: "Yes",
    a: "Yes. Foot traffic and visitor journey estimates come with a confidence score, every place is scored on how reliably its visits can be estimated, and the method behind both is public in the Docs.",
  },
  {
    // Self-served and fast, not "drawing" (Sarah, via Anna 2026-11-04): API
    // clients send the shapes, they don't draw them. Custom locations work in
    // every market (market-capabilities).
    q: "Can we easily get data for our own custom areas?",
    short: "Yes",
    a: "Yes, self-served. Add the area in Insights or send its shape through the API, and the data comes back without a request to us.",
  },
]

// Under the title, here and in the article card.
const LEDE = "Ask them before you choose your next data partner."

// What the copy button puts on the clipboard: ready to paste into an email.
const EMAIL_TEXT = [
  "Before we decide on our data partner, could you answer these five questions?",
  "",
  ...CHECKS.map(({ q }, i) => `${i + 1}. ${q}`),
].join("\n")

// Clipboard API where available, the old textarea + execCommand path where it
// isn't (older Safari, non-secure contexts). Resolves false if both fail.
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement("textarea")
      ta.value = text
      ta.setAttribute("readonly", "")
      ta.style.position = "absolute"
      ta.style.left = "-9999px"
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand("copy")
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}

type CopyState = "idle" | "copied" | "failed"

// Copies the questions and says so for a moment. `onCopy` is the tracking.
function useCopyQuestions(onCopy: () => void) {
  const [state, setState] = useState<CopyState>("idle")

  useEffect(() => {
    if (state === "idle") return
    const t = window.setTimeout(() => setState("idle"), 2500)
    return () => window.clearTimeout(t)
  }, [state])

  const copy = async () => {
    onCopy()
    setState((await copyText(EMAIL_TEXT)) ? "copied" : "failed")
  }
  return { state, copy }
}

// The ground the list sits on: the forest section on the conquest page, or a
// light card inside an article.
type Tone = "dark" | "light"

const TONE: Record<
  Tone,
  {
    rule: string
    // the question and its short answer
    row: string
    q: string
    mark: string
    short: string
    chevron: string
    a: string
    link: string
    ring: string
  }
> = {
  dark: {
    rule: "border-paper/15",
    row: "items-start gap-4",
    q: "text-paper",
    mark: "/brand/logo-symbol-lime.svg",
    short: "text-lime",
    chevron: "text-paper/50 group-hover:text-paper",
    a: "text-paper/70",
    link: "text-lime hover:text-hay",
    ring: "focus-visible:ring-lime",
  },
  light: {
    rule: "border-line",
    // An article card is narrower than the conquest section, so on phones
    // the short answer sits under the question instead of squeezing it.
    row: "flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-4",
    q: "text-ink",
    // chartreuse mark, chartreuse-deep word: the deep one passes AA on cream
    mark: "/brand/logo-symbol-green.svg",
    short: "text-chartreuse-deep",
    chevron: "text-ink/40 group-hover:text-ink",
    a: "text-mute",
    link: "text-chartreuse-deep hover:text-forest",
    ring: "focus-visible:ring-forest",
  },
}

// The five questions as a quiet list with hairlines, one answer open at a
// time. The first starts open (Anna 2026-11-05: "unfold first answer by
// default"), so the pattern of a question and its Yes reads before anyone
// clicks.
function QuestionList({
  tone,
  id,
  className,
  named = false,
}: {
  tone: Tone
  // prefix for the answers' ids, unique on the page
  id: string
  className?: string
  // shows each answer's comparison with named providers (the conquest
  // page; the blog card names no one)
  named?: boolean
}) {
  const t = TONE[tone]
  const [open, setOpen] = useState<number | null>(0)

  return (
    <ul className={cn("border-t", t.rule, className)}>
      {CHECKS.map((check, i) => {
        const { q, short, a } = check
        const on = open === i
        return (
          <li key={q} className={cn("border-b", t.rule)}>
            <button
              type="button"
              aria-expanded={on}
              aria-controls={`${id}-${i}`}
              onClick={() => setOpen(on ? null : i)}
              className={cn(
                "group flex w-full items-start gap-4 py-5 text-left focus-visible:outline-none focus-visible:ring-2",
                t.ring,
              )}
            >
              <span className={cn("flex flex-1", t.row)}>
                <span
                  className={cn(
                    "flex-1 font-sans text-[16px] font-medium leading-snug sm:text-[17px]",
                    t.q,
                  )}
                >
                  {q}
                </span>
                {/* Show, don't tell (Anna 2026-11-02): the mark says whose
                    answer it is. */}
                <span
                  className={cn(
                    "mt-0.5 flex shrink-0 items-center gap-1.5 text-[14px] font-semibold",
                    t.short,
                  )}
                >
                  <img
                    src={t.mark}
                    alt=""
                    aria-hidden
                    width={102}
                    height={42}
                    className="h-3 w-auto"
                  />
                  <span className="sr-only">Pine59: </span>
                  {short}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  "mt-0.5 h-5 w-5 shrink-0 transition-transform duration-300",
                  t.chevron,
                  on && "rotate-180",
                )}
                strokeWidth={1.75}
                aria-hidden
              />
            </button>
            {/* grid-rows trick: the answer opens and closes smoothly */}
            <div
              id={`${id}-${i}`}
              className={cn(
                "grid transition-all duration-300 ease-out",
                on
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0",
              )}
            >
              <p
                className={cn(
                  "overflow-hidden pr-10 text-[15px] leading-relaxed",
                  t.a,
                )}
              >
                <span className="block pb-5">
                  {a}
                  {named && check.vs ? (
                    <span className="mt-2 block">{check.vs}</span>
                  ) : null}
                  {check.link ? (
                    <Link
                      to={check.link.href}
                      tabIndex={on ? undefined : -1}
                      className={cn(
                        "group mt-2 flex w-fit items-center gap-1 font-medium",
                        t.link,
                      )}
                    >
                      {check.link.label}
                      <ArrowRight
                        className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                        strokeWidth={2}
                        aria-hidden
                      />
                    </Link>
                  ) : null}
                </span>
              </p>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function CopyButton({
  tone,
  state,
  onClick,
}: {
  tone: Tone
  state: CopyState
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        tone === "dark"
          ? "inline-flex items-center justify-center gap-2 rounded-[4px] px-4 py-4 font-sans text-[16px] font-medium leading-[1.2] text-cream shadow-[inset_0_0_0_1px_#66640F] transition-colors hover:bg-moss focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-forest"
          : // the site's secondary button, allowed to wrap: the "couldn't
            // copy" line is longer than a phone-width card
            cn(
              buttonClasses({ variant: "secondary" }),
              "gap-2 whitespace-normal text-left focus-visible:ring-offset-cream",
            )
      }
    >
      {state === "copied" ? (
        <Check className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
      ) : (
        <Copy className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
      )}
      <span aria-live="polite">
        {state === "copied"
          ? "Copied, ready to paste"
          : state === "failed"
            ? "Couldn't copy, select the questions instead"
            : "Copy the questions"}
      </span>
    </button>
  )
}

export function RenewalCheck() {
  const track = useTrack()
  const { state, copy } = useCopyQuestions(track("alternative-renewal-copy"))

  return (
    <section className="relative overflow-hidden bg-forest py-16 text-paper sm:py-24">
      {/* Our usual dark grid (topo-grid-dark, as in the site banner), kept
          subtle (Anna 2026-11-04: "our usual grid background but subtle",
          in place of the treeline): low opacity, and a forest wash from the
          right so the questions and answers sit on near-plain ground while
          the texture shows through on the left, behind the big heading (Anna
          2026-11-05: the pattern is "not a nice backdrop" for small text, so
          it is mirrored to the other side). Sized by the width only and
          pinned to the top, fading out at its foot, so opening an answer
          (which makes the section taller) never rescales or moves it (Anna
          2026-11-04: "the background jumps"). */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <img
          src={BANNER_ART}
          srcSet="/art/topo-grid-dark-800.webp 800w, /art/topo-grid-dark-1200.webp 1200w, /art/topo-grid-dark.webp 1600w"
          sizes="(max-width: 1100px) 1100px, 100vw"
          alt=""
          width={1600}
          height={1067}
          loading="lazy"
          decoding="async"
          className="absolute left-0 top-0 h-auto w-[max(100%,1100px)] max-w-none -scale-x-100 opacity-40 [mask-image:linear-gradient(to_bottom,black_65%,transparent)]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-forest via-forest/70 to-forest/20" />
      </div>
      {/* Header, list, actions in that order on phones; from lg the list
          takes the right column and the actions sit under the header. */}
      <div className="relative u-container grid max-w-6xl gap-x-16 gap-y-8 lg:grid-cols-[5fr_7fr] lg:grid-rows-[auto_1fr]">
        <div className="lg:col-start-1 lg:row-start-1">
          <h2 className="font-sans text-[clamp(28px,3.4vw,44px)] font-semibold leading-[1.04] tracking-[-0.035em] text-hay">
            5 questions to send your provider
          </h2>
          <p className="mt-4 text-[18px] leading-snug text-paper/65">
            {/* Names who "your provider" is, live since Thomas's "we can be
                more aggressive on names" (2026-11-04): a plain mention, no
                claim about either. */}
            Placer.ai, Advan or anyone else: {LEDE.toLowerCase()}
          </p>
        </div>

        {/* No `named` for now: the line on Placer.ai's and Advan's public
            terms under Q1 stays off until Thomas has read it (Anna
            2026-11-05: launch without it). Add `named` back once he signs
            off. */}
        <QuestionList
          tone="dark"
          id="renewal-a"
          className="lg:col-start-2 lg:row-span-2 lg:row-start-1"
        />

        {/* The action: take the questions away. The CTA is for visitors who
            would rather test than ask. */}
        <div className="flex flex-col gap-3 sm:flex-row lg:col-start-1 lg:row-start-2 lg:flex-col lg:items-start lg:self-start xl:flex-row">
          <CopyButton tone="dark" state={state} onClick={copy} />
          <ButtonLink
            href={`#${BOOK_ID}`}
            onClick={track("alternative-renewal-check")}
          >
            Test your locations
          </ButtonLink>
        </div>
      </div>
    </section>
  )
}

const TITLE = "5 questions to send your provider"

// The five questions inside an article: a light card in the post's column,
// not a dark band (the post already has its forest Get access card), with
// the copy button and a link to the conquest page. Its heading is an h2 with
// an id, so it shows in the post's contents rail like any other section.
// Names no provider, not even on previews.
//
// Clicks are `explore_data_clicked` with the post path, like BlogCta's.
// `?from=blog` keeps these visits apart from the ad traffic on the page.
export function ProviderQuestions({
  href = "/foot-traffic-data-alternative?from=blog",
}: {
  href?: string
}) {
  const { pathname } = useLocation()
  const track = (location: string) => () =>
    capture("explore_data_clicked", { location, post: pathname })
  const { state, copy } = useCopyQuestions(track("blog_questions_copy"))

  return (
    <section
      aria-labelledby={slugify(TITLE)}
      className="mt-14 rounded-xl bg-cream px-6 py-7 sm:px-8 sm:py-8"
    >
      <h2
        id={slugify(TITLE)}
        className="scroll-mt-28 font-sans text-2xl font-semibold leading-snug tracking-tight text-ink"
      >
        {TITLE}
      </h2>
      <p className="mt-2 text-[17px] leading-relaxed text-mute">{LEDE}</p>
      <QuestionList tone="light" id="provider-q" className="mt-6" />
      <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
        <CopyButton tone="light" state={state} onClick={copy} />
        <Link
          to={href}
          onClick={track("blog_questions_switch")}
          className="group inline-flex items-center gap-1 text-[16px] font-medium text-chartreuse-deep hover:text-forest"
        >
          How switching works
          <ArrowRight
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
            aria-hidden
          />
        </Link>
      </div>
    </section>
  )
}
